import React, { useState, useEffect } from 'react'
import {
  Card,
  Table,
  Tag,
  Space,
  Input,
  DatePicker,
  Select,
  Timeline,
  Modal,
  Descriptions,
  Button,
  message
} from 'antd'
import {
  SearchOutlined,
  UserOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons'
import moment from 'moment'
import { getAuditTrail, getAuditDetails, deleteAllAuditTrail } from '../../backend-integration/api/auditTrail'
import { BillingEnums } from '../../utils/enums'

const { RangePicker } = DatePicker
const { Option } = Select

const AuditTrailViewer = ({ accountId, entityType }) => {
  const [loading, setLoading] = useState(false)
  const [auditEntries, setAuditEntries] = useState([])
  const [filters, setFilters] = useState({ accountId, entityType })
  const [selectedEntry, setSelectedEntry] = useState(null)
  const [detailsModalVisible, setDetailsModalVisible] = useState(false)
  const [deletingAll, setDeletingAll] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  })

  useEffect(() => {
    fetchAuditTrail()
  }, [pagination.current, pagination.pageSize, filters])

  const fetchAuditTrail = async () => {
    setLoading(true)
    try {
      const { data, total } = await getAuditTrail({
        ...filters,
        page: pagination.current,
        limit: pagination.pageSize
      })
      setAuditEntries(data)
      setPagination((prev) => ({ ...prev, total }))
    } catch (error) {
      console.error('Error fetching audit trail:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (value) => {
    setFilters((prev) => ({ ...prev, search: value }))
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  const handleDateFilter = (dates) => {
    if (dates) {
      setFilters((prev) => ({
        ...prev,
        startDate: dates[0].toDate(),
        endDate: dates[1].toDate()
      }))
    } else {
      setFilters((prev) => {
        const { startDate, endDate, ...rest } = prev
        return rest
      })
    }
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  const handleActionFilter = (action) => {
    setFilters((prev) => ({ ...prev, action }))
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  const showDetails = async (entry) => {
    setSelectedEntry(entry)
    setDetailsModalVisible(true)
  }

  const handleDeleteAllAuditTrail = () => {
    Modal.confirm({
      title: 'Delete All Audit Trail Data',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>
            <strong>⚠️ WARNING: This action cannot be undone!</strong>
          </p>
          <p>This will permanently delete ALL audit trail entries from the system.</p>
          <p>Are you sure you want to proceed?</p>
        </div>
      ),
      okText: 'Yes, Delete All',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setDeletingAll(true)
        try {
          await deleteAllAuditTrail()
          message.success('All audit trail data has been deleted successfully')
          // Refresh the audit trail view
          fetchAuditTrail()
        } catch (error) {
          console.error('Error deleting audit trail:', error)
          message.error('Failed to delete audit trail data: ' + error.message)
        } finally {
          setDeletingAll(false)
        }
      }
    })
  }

  const getActionColor = (action) => {
    const colors = {
      CREATE: 'success',
      UPDATE: 'processing',
      DELETE: 'error',
      APPROVE: 'green',
      REJECT: 'red',
      PAYMENT: 'blue',
      REFUND: 'orange',
      WRITE_OFF: 'purple'
    }
    return colors[action] || 'default'
  }

  const getChangeDescription = (entry) => {
    if (entry.changes && entry.changes.length > 0) {
      return entry.changes.map((change) => (
        <div key={change.field}>
          <strong>{change.field}:</strong> {change.oldValue} → {change.newValue}
        </div>
      ))
    }
    return entry.description
  }

  const columns = [
    {
      title: 'Date & Time',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp) => moment(timestamp).format('YYYY-MM-DD HH:mm:ss'),
      width: 160
    },
    {
      title: 'User',
      dataIndex: 'userName',
      key: 'userName',
      render: (name, record) => (
        <Space>
          <UserOutlined />
          {name || record.userId}
        </Space>
      )
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action) => <Tag color={getActionColor(action)}>{action}</Tag>
    },
    {
      title: 'Entity',
      dataIndex: 'entityType',
      key: 'entityType',
      render: (type, record) => (
        <Space>
          <FileTextOutlined />
          {type} ({record.entityId})
        </Space>
      )
    },
    {
      title: 'Changes',
      key: 'changes',
      render: (_, record) => <div style={{ maxWidth: 400 }}>{getChangeDescription(record)}</div>,
      ellipsis: true
    },
    {
      title: 'IP Address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 120
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button type='link' onClick={() => showDetails(record)}>
          View Details
        </Button>
      )
    }
  ]

  const renderDetailsModal = () => {
    if (!selectedEntry) return null

    return (
      <Modal
        title='Audit Trail Details'
        visible={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        footer={null}
        width={700}
      >
        <Descriptions bordered column={2}>
          <Descriptions.Item label='Date & Time' span={2}>
            {moment(selectedEntry.timestamp).format('YYYY-MM-DD HH:mm:ss')}
          </Descriptions.Item>
          <Descriptions.Item label='User'>{selectedEntry.userName || selectedEntry.userId}</Descriptions.Item>
          <Descriptions.Item label='IP Address'>{selectedEntry.ipAddress}</Descriptions.Item>
          <Descriptions.Item label='Action'>
            <Tag color={getActionColor(selectedEntry.action)}>{selectedEntry.action}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label='Entity Type'>{selectedEntry.entityType}</Descriptions.Item>
          <Descriptions.Item label='Entity ID' span={2}>
            {selectedEntry.entityId}
          </Descriptions.Item>
          {selectedEntry.reason && (
            <Descriptions.Item label='Reason' span={2}>
              {selectedEntry.reason}
            </Descriptions.Item>
          )}
        </Descriptions>

        {selectedEntry.changes && selectedEntry.changes.length > 0 && (
          <>
            <h4 style={{ marginTop: 16 }}>Changes</h4>
            <Timeline>
              {selectedEntry.changes.map((change, index) => (
                <Timeline.Item key={index}>
                  <strong>{change.field}:</strong>
                  <br />
                  <span style={{ color: '#ff4d4f' }}>{JSON.stringify(change.oldValue)}</span>
                  {' → '}
                  <span style={{ color: '#52c41a' }}>{JSON.stringify(change.newValue)}</span>
                </Timeline.Item>
              ))}
            </Timeline>
          </>
        )}

        {selectedEntry.metadata && (
          <>
            <h4 style={{ marginTop: 16 }}>Additional Information</h4>
            <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
              {JSON.stringify(selectedEntry.metadata, null, 2)}
            </pre>
          </>
        )}
      </Modal>
    )
  }

  return (
    <Card title='Audit Trail'>
      <Space style={{ marginBottom: 16 }} wrap>
        <Input.Search
          placeholder='Search by user or description'
          onSearch={handleSearch}
          style={{ width: 300 }}
          prefix={<SearchOutlined />}
        />
        <RangePicker onChange={handleDateFilter} format='YYYY-MM-DD' />
        <Select placeholder='Filter by action' style={{ width: 150 }} allowClear onChange={handleActionFilter}>
          <Option value='CREATE'>Create</Option>
          <Option value='UPDATE'>Update</Option>
          <Option value='DELETE'>Delete</Option>
          <Option value='APPROVE'>Approve</Option>
          <Option value='REJECT'>Reject</Option>
          <Option value='PAYMENT'>Payment</Option>
          <Option value='REFUND'>Refund</Option>
          <Option value='WRITE_OFF'>Write-off</Option>
        </Select>
        <Input
          placeholder='Filter by Account/Billing ID'
          style={{ width: 200 }}
          allowClear
          onChange={(e) => {
            const value = e.target.value
            setFilters((prev) => ({
              ...prev,
              accountId: value || undefined
            }))
            setPagination((prev) => ({ ...prev, current: 1 }))
          }}
        />
        <Button
          type='primary'
          danger
          icon={<DeleteOutlined />}
          loading={deletingAll}
          onClick={handleDeleteAllAuditTrail}
        >
          Delete All
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={auditEntries}
        rowKey='_id'
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} audit entries`,
          onChange: (page, pageSize) => {
            setPagination((prev) => ({
              ...prev,
              current: page,
              pageSize
            }))
          }
        }}
      />

      {renderDetailsModal()}
    </Card>
  )
}

export default AuditTrailViewer
